import { useDispatch, useSelector } from "react-redux";
import Walls from "./Walls";
import Floor from "./Floor";
import { useCallback, useState, useRef, useEffect, createContext } from "react";
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import Ceiling from "./Ceiling";
import BuilderSelect from "@/store/builderStoreSelector";
import { BuilderProduct } from "@/store/builderSlice";
import { actions as builderActions } from "@/store/builderSlice";
import { ProductPlacementType, SurfaceType } from "@/utils/builder/builderConstants";
import { useGLTF } from "@react-three/drei";
import { GLTFExporter } from 'three-stdlib';
import { Scene } from 'three';
import axios from "axios";
import { Html } from '@react-three/drei';
import { getUserId, setUserId } from "@/utils/builder/userUtils";
import PreviewComponent from "./PreviewComponent";
import {useRouter} from 'next/router'
import { actions } from '@/store/builderSlice';
export interface RoomViewRendererProps {
    roomModelLoaded: Function;
    toggleControls: Function;
    updateCameraPosition: Function;
}

interface BakedRoomModelProps {
    url: string;
}
export const BakedRoomModel: React.FC<BakedRoomModelProps> = ({ url }) => {
    const bakedGLBscene = useGLTF(url);
    return <primitive object={bakedGLBscene.scene} />;
};

const RoomViewRenderer: React.FC<RoomViewRendererProps> = (
    { roomModelLoaded, toggleControls, updateCameraPosition }) => {
        console.log('room-view renderer component------------------------')
    const dispatch = useDispatch();
    const [roomBoundingBox, setRoomBoundingBox] = useState(new THREE.Box3());
    const centerRef = useRef<THREE.Vector3 | undefined>(undefined);
    const roomProductsRef = useRef<THREE.Object3D[]>([]);
    const floorGeometryRef = useRef<any>();
    const floorMeshRef = useRef<THREE.Mesh | undefined>(undefined);
    const defaultProductAdded = useRef(false);
    const products: BuilderProduct[] = BuilderSelect.productContent().products;
    const urlParams: { [key: string]: string } = BuilderSelect.ui().urlParams;
    const isEditFlow = urlParams.projectId != null;
    const curProduct = products.find(product => product.productId === urlParams.productId);
    const isPreviewMode = BuilderSelect.ui().viewMode === 'preview';
    const [isBakedGLBReady, setIsBakedGLBReady] = useState(false);
    let unbakedGLBPresignedURL = "";
    const [previewGLBDownloadURL, setPreviewGLBDownloadURL] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const isStartGeneratePreview=BuilderSelect.generatePreview().startGeneratePreview;
    const router=useRouter()
    const [floorTexture, setFloorTexture] = useState<THREE.Texture | undefined>(undefined);
    const isShowcase = urlParams.showcase == 'true';
    const roomPositionY = isShowcase ? -2 : 0;

    const addDefaultProduct = () => {
        if (!isEditFlow && curProduct && centerRef.current && !defaultProductAdded.current) {
            defaultProductAdded.current = true;
            dispatch(builderActions.addProductToProject({
                productId: curProduct.productId,
                surfaceType: SurfaceType.FLOOR,
                surfaceId: SurfaceType.FLOOR,
                type: ProductPlacementType.Product,
                undoable: true,
                position: centerRef.current
            }));
        }
    }

    const onRoomModelLoaded = useCallback((boundingBox: THREE.Box3, floorGeometry: any,
        floorMesh?: THREE.Mesh,
        roomProducts?: THREE.Object3D[]) => {
        roomModelLoaded();
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);
        centerRef.current = center;
        roomProductsRef.current = roomProducts ? roomProducts : [];
        floorGeometryRef.current = floorGeometry;
        floorMeshRef.current = floorMesh;
        addDefaultProduct();
        setRoomBoundingBox(boundingBox);
    }, []);

    // Load the preview room GLB model
    //const previewRoomModel = useGLTF('./floors/NewRoom_4K_3.glb');

    // Get the scene from useThree
    const { scene } = useThree();

    useEffect(() => {
      const link = document.createElement("a");
      link.style.display = "none";
      document.body.appendChild(link);
    }, []);
  
    function save(blob: Blob, filename: string) {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    }
  
  
    function exportAndUploadGLB() {
        if (!scene) return;

        const exporter = new GLTFExporter();

        // Create a new scene or group to hold only the objects you want to export
        const exportScene = scene.clone();
        
        // Example: Add specific objects (e.g., meshes) to this new scene
        exportScene.traverse((object) => {
            if (object instanceof THREE.Mesh) {
                // Example: Include only certain objects by name or custom condition
                if (object.geometry.type == "PlaneGeometry" || object.geometry.type == "LineGeometry") {
                    object.visible = false;
                }
            }
            if (object.type == "GridHelper") {
                object.visible = false
            }
        });

        exporter.parse(
            exportScene, (gltf) => {
                // gltf is an ArrayBuffer
                console.log("Debug -> GLB export successful");
                
                uploadToS3(gltf);
            }, function (error) {
                console.log('An error happened', error);
            },
            { 
                binary: true,
                onlyVisible: true
            }    
        );
    }
    const arrayBufferToBlob = (buffer: any) => {
        return new Blob([buffer], { type: 'application/octet-stream' });
    };
    const uploadToS3 = async (gltfBuffer: any) => {
        const blob = arrayBufferToBlob(gltfBuffer);
        
        // Assume you have a function to get the presigned URL from your backend
        const presignedUrl = unbakedGLBPresignedURL;
        
        try {
            const response = await fetch(presignedUrl, {
                method: 'PUT',
                body: blob,
                headers: {
                    'Content-Type': 'model/gltf-binary',
                },
            });
            console.log('Upload Response', response);
            if (response.ok) {
                console.log('Upload successful');
                dispatch(builderActions.startGeneratePreview({startGeneratePreview:true }));

                router.push('/projects')
                // setIsBakedGLBReady(true);
                setIsLoading(false);

                // fetchPreviewGenerationStatusRequest();
            } else {
                console.error('Upload failed');
            }
        } catch (error) {
            console.error('Error uploading to S3:', error);
        }
    };

    useEffect(() => {
        if (isStartGeneratePreview) {
            setIsLoading(true);
            handlePutRequest();
            // downloadGLB();
        }
    }, [isStartGeneratePreview, scene]);

    const fetchPreviewGenerationStatusRequest = async () => {
        const projectID = urlParams.projectId; //'ff1dbbc6-bc99-41b2-b3ae-95a06a18299d';
        const customerID = getUserId();
        const url = 'https://d221a61rb87vel.cloudfront.net/v1/api/projects/getPreview/' + projectID; 
    
        try {
          const res = await axios.get(url, {
            headers: {
              'Content-Type': 'application/json',
              'Customer-Id': customerID //"b0a4cdea-5e58-46e0-a345-8c7bffc1591e"
            }
          });
          
          console.log('Get Preview Response:', res.data);
          if (res?.data?.previewStatus&&res?.data?.previewImageUrl) {
            setPreviewGLBDownloadURL(res.data.previewImageUrl);
            setIsBakedGLBReady(true);
            setIsLoading(false);
          }
          
        } catch (error) {
          console.log('Error making GET request', error);
        }

    };

    useEffect(() => {
        if (isPreviewMode) {
            // dispatch(actions.setViewMode({ viewMode: 'preview' }))
            setIsLoading(true);
            fetchPreviewGenerationStatusRequest();
        }
    }, [isPreviewMode])
    const handlePutRequest = async () => {
        const projectID = urlParams.projectId; //'ff1dbbc6-bc99-41b2-b3ae-95a06a18299d';
        const customerID = getUserId();
        const url = 'https://d221a61rb87vel.cloudfront.net/v1/api/projects/generatePreview/' + projectID; 
        
        // Data you want to send in the request body
        const data = { };
    
        try {
          const res = await axios.put(url, data, {
            headers: {
              'Content-Type': 'application/json',
              'Customer-Id': customerID //"b0a4cdea-5e58-46e0-a345-8c7bffc1591e"
            }
          });
          //setResponse(res.data);
          console.log('Generate Preview Response:', res.data);
          unbakedGLBPresignedURL = res.data.unbakedImageSignedUrl;
        //   setPreviewGLBDownloadURL('https://d221a61rb87vel.cloudfront.net/projectImages/b0a4cdea-5e58-46e0-a345-8c7bffc1591e1e2864f8-749c-4186-8251-3c888fc3a20d_previewImage.glb'); //setPreviewGLBDownloadURL(res.data.previewImageUrl);
          
          exportAndUploadGLB(); // <-- Working
        } catch (error) {
          console.error('Error making PUT request', error);
        }
    };

    useEffect(() => {
        addDefaultProduct();
    }, [curProduct]);

    // if (isLoading) {
    //     return (
    //       <Html center>
    //         <div className="loader">
    //           Loading...
    //         </div>
    //       </Html>
    //     );
    // }

    return <>
        {!(isBakedGLBReady) && <group position={[0, roomPositionY, 0]}>
            <Floor
                boundingBox={roomBoundingBox}
                toggleControls={toggleControls}
                updateCameraPosition={updateCameraPosition}
                geometry={floorGeometryRef.current}
                floorTexture={floorTexture} // Added this line to preserve sample room floor texture
                floorMesh={floorMeshRef.current}
            />
            <Walls roomModelLoaded={onRoomModelLoaded} setFloorTexture={setFloorTexture} toggleControls={toggleControls} />
            {/* <Ceiling boundingBox={roomBoundingBox} /> */}
        </group>}

        {isPreviewMode && isBakedGLBReady && <group>
            {/* <primitive object={previewRoomModel.scene}></primitive> */}
            <BakedRoomModel url={previewGLBDownloadURL} />
            {/* <PreviewComponent /> */}
        </group>}
    </>
}

export default RoomViewRenderer;
